// Learning from your own catches: for each species you have caught at least MIN_CATCHES times (with weather saved),
// find the conditions most of those catches had: time of day, wind, air pressure trend, water temperature, moon.
// Shown as a card on the Catches tab, and used by the "matches your best catches" tip (shared/tip-rules.ts).
import type { Catch, Weather } from "../shared/model.ts";
import type { LearnedPattern, Trend } from "../shared/tip-rules.ts";
import { $maybe, el, icon } from "./dom.ts";
import { LANG, fmtNum, list, t } from "./i18n.ts";
import { speciesName } from "./species.ts";
import { catches, onRender } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { hasWater } from "./water.ts";
import { pushProfileChanged } from "./push.ts";
import { hasPlus } from "./billing.ts";
import { openPaywall } from "./paywall.ts";

const MIN_CATCHES = 5;
/** The value at fraction `p` (0–1) of the sorted list. */
const pct = (arr: number[], p: number) => { const s = [...arr].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.max(0, Math.round((s.length - 1) * p)))]; };
const catchKey = (c: Catch) => c.speciesId || (c.species || "").trim().toLowerCase();
type WithWeather = Catch & { weather: Weather };

interface Detail {
  fromH: number;
  toH: number;
  /** Share of catches in the best 6-hour window. */
  timeShare: number;
  windMax: number;
  trend: Trend | null;
  trendShare: number;
  /** Usual water temperature range, °C. */
  water: [number, number] | null;
  /** Moon phase index 0–7 when at least half the catches were around it. */
  moonPhase: number | null;
}
export type Learned = LearnedPattern & { detail: Detail };

function learnSpecies(list: WithWeather[]): Detail {
  const n = list.length;
  // best 6-hour stretch of the day (it may run past midnight)
  const hours = list.map((c) => new Date(c.time).getHours());
  let best = { from: 0, count: -1, spread: Infinity };
  for (let from = 0; from < 24; from++) {
    const inside = hours.filter((h) => (h - from + 24) % 24 < 6);
    // ties go to the window that has the catches nearest its middle
    const spread = inside.reduce((s, h) => s + Math.abs(((h - from + 24) % 24) + 0.5 - 3), 0);
    if (inside.length > best.count || (inside.length === best.count && spread < best.spread)) best = { from, count: inside.length, spread };
  }
  const winds = list.map((c) => c.weather.wind).filter((v): v is number => v != null);
  const windMax = winds.length ? Math.max(2, Math.ceil(pct(winds, 0.8))) : 30;
  const trends: Trend[] = list.map((c) => c.weather.pressure3h).filter((v): v is number => v != null).map((d) => (d <= -1 ? "falling" : d >= 1 ? "rising" : "steady"));
  let trend: Trend | null = null, trendShare = 0;
  for (const k of ["falling", "steady", "rising"] as const) { const s = trends.filter((x) => x === k).length / (trends.length || 1); if (s > trendShare) { trendShare = s; trend = k; } }
  if (trends.length < 4 || trendShare < 0.6) trend = null;
  const waters = list.flatMap((c) => (hasWater(c) ? [c.water.value] : []));
  const water: [number, number] | null = waters.length >= 3 ? [pct(waters, 0.2), pct(waters, 0.8)] : null;
  const P = 29.530588853;
  const phases = list.map((c) => Math.round(((((c.time - Date.UTC(2000, 0, 6, 18, 14)) / 864e5) % P + P) % P) / P * 8) % 8);
  let moonPhase: number | null = null;
  for (let ph = 0; ph < 8; ph++) { if (phases.filter((x) => x === ph || x === (ph + 1) % 8).length / n >= 0.5) { moonPhase = ph; break; } }
  return { fromH: best.from, toH: (best.from + 6) % 24, timeShare: best.count / n, windMax, trend, trendShare, water, moonPhase };
}

let cache: { key: string; list: Learned[] } = { key: "", list: [] };
/** Patterns for every species with enough catches, most caught first (at most six). */
export function learnedPatterns(): Learned[] {
  const withWx = catches.filter((c): c is WithWeather => c.weather?.status === "ok");
  const key = LANG + ":" + withWx.map((c) => c.id + c.time).join(",");
  if (cache.key === key) return cache.list;
  const bySpecies = new Map<string, WithWeather[]>();
  for (const c of withWx) { const k = catchKey(c); if (!k) continue; if (!bySpecies.has(k)) bySpecies.set(k, []); bySpecies.get(k)!.push(c); }
  const out: Learned[] = [];
  for (const [k, cs] of bySpecies) {
    if (cs.length < MIN_CATCHES) continue;
    const d = learnSpecies(cs);
    const two = (h: number) => String(h).padStart(2, "0");
    const parts = [t("learnTime", two(d.fromH), two(d.toH)), t("learnWind", d.windMax)];
    if (d.trend) parts.push(t(`learnTrend_${d.trend}`));
    out.push({ species: k, kind: cs[0].kind, name: speciesName(cs[0]), n: cs.length, fromH: d.fromH, toH: d.toH, windMax: d.windMax, trend: d.trend,
      summary: parts.join(", "), detail: d });
  }
  out.sort((a, b) => b.n - a.n);
  cache = { key, list: out.slice(0, 6) };
  return cache.list;
}

function renderInsights(): void {
  const box = $maybe("#insights");
  if (!box) return;
  const learned = learnedPatterns();
  if (!learned.length) {
    // a hint once there are some catches, so people know this is coming
    const most = Math.max(0, ...[...new Set(catches.map(catchKey))].map((k) => catches.filter((c) => catchKey(c) === k && c.weather?.status === "ok").length));
    if (catches.length >= 2 && most < MIN_CATCHES) { box.replaceChildren(el("p", { class: "hint insight-hint", text: t("learnNotYet", MIN_CATCHES, most) })); box.hidden = false; }
    else box.hidden = true;
    return;
  }
  if (!hasPlus()) {
    // ReelHunt Plus: the free version shows what has been learned about, not the conditions themselves
    const names = new Intl.ListFormat(LANG, { type: "conjunction" }).format(learned.map((L) => L.name.toLowerCase()));
    box.replaceChildren(el("div", { class: "tipcard locked" },
      el("div", { class: "tipshead" }, el("b", { text: t("learnTitle") }), el("span", { class: "plus-tag" }, icon("star"), t("plusBadge"))),
      el("p", { text: t("learnPlusText", names) }),
      el("button", { class: "btn primary small", type: "button", onclick: () => openPaywall("plus", { reason: t("payWhyBest") }) }, t("plusSee"))));
    box.hidden = false;
    return;
  }
  const moonNames = list("moonPhases"), two = (h: number) => String(h).padStart(2, "0");
  box.replaceChildren(el("div", { class: "tipcard" },
    el("div", { class: "tipshead" }, el("b", { text: t("learnTitle") })),
    ...learned.map((L) => {
      const d = L.detail, lines = [
        t("learnTimeLong", two(L.fromH), two(L.toH), Math.round(d.timeShare * 100)),
        t("learnWindLong", L.windMax),
        d.trend ? t(`learnTrendLong_${d.trend}`, Math.round(d.trendShare * 100)) : null,
        d.water ? t("learnWater", fmtNum(d.water[0], 0), fmtNum(d.water[1], 0)) : null,
        d.moonPhase != null ? t("learnMoon", moonNames[d.moonPhase].toLowerCase()) : null,
      ].filter((x): x is string => x != null);
      return el("div", { class: "insight" }, icon(L.kind === "game" ? "game" : "fish"), el("div", {},
        el("b", { text: t("learnSpecies", L.name, L.n) }), el("ul", {}, ...lines.map((x) => el("li", { text: x })))));
    }),
    el("p", { class: "tipsfoot", text: t("learnFoot") })));
  box.hidden = false;
}

onRender(() => {
  renderInsights();
  // the evening tips use these patterns too; resend when they change
  const sig = JSON.stringify([hasPlus(), learnedPatterns().map((L) => [L.species, L.n, L.fromH, L.windMax, L.trend])]);
  if (sig !== lsGet("learnedSig", "")) { lsSet("learnedSig", sig); pushProfileChanged(); }
});
